import { useCallback, useEffect, useState } from "react";
import { Calendar, Clock, MapPin, Loader2, Plus, Trash2, Users } from "lucide-react";

// Self-service "manage my booking" (/booking/manage?token=…), reached from the
// link in every confirmation email.
//
// The point of this page is to REMOVE the reason people register twice: until
// now a devotee who wanted to bring two more relatives had no way to edit, so
// they submitted a second registration — creating the duplicates that show up
// in the admin desk and quietly consume seats.

// Event dates are date-only values stored at midnight UTC; render in UTC so the
// day never shifts for a viewer west of the meridian.
const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
};

export default function ManageBooking() {
  const base = import.meta.env.VITE_APP_API_BASE_URL || "";
  const token = new URLSearchParams(window.location.search).get("token") || "";

  const [booking, setBooking] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | notfound | error
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [names, setNames] = useState([]);
  const [additions, setAdditions] = useState([]);

  const applyBooking = useCallback((data) => {
    setBooking(data);
    setNames((data.attendees || []).map((a) => a.name));
    setAdditions([]);
  }, []);

  useEffect(() => {
    if (!token) {
      setState("notfound");
      return;
    }
    let cancelled = false;
    fetch(`${base}/manage?token=${encodeURIComponent(token)}`)
      .then(async (r) => ({ ok: r.ok, body: await r.json().catch(() => ({})) }))
      .then(({ ok, body }) => {
        if (cancelled) return;
        if (!ok || !body?.success) {
          setState(body?.message === "Booking not found" ? "notfound" : "error");
          return;
        }
        applyBooking(body.data);
        setState("ready");
      })
      .catch(() => !cancelled && setState("error"));
    return () => {
      cancelled = true;
    };
  }, [base, token, applyBooking]);

  const send = async (payload, successFallback) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const res = await fetch(`${base}/manage`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, ...payload }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) {
        setError(body?.message || "Something went wrong. Please try again.");
        return false;
      }
      if (body.data) applyBooking(body.data);
      setNotice(body.message || successFallback);
      return true;
    } catch {
      setError("Could not reach the server. Please try again.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Cancel the ENTIRE booking. Separate endpoint from the per-guest remove
  // above, because it releases every seat at once and closes the record.
  const cancelAll = async () => {
    if (
      !window.confirm(
        `Cancel this booking completely and release all ${booking?.partySize || ""} seats? This cannot be undone.`
      )
    )
      return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const res = await fetch(`${base}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) {
        setError(body?.message || "Could not cancel the booking. Please try again.");
        return;
      }
      setBooking((b) => ({ ...b, status: "Cancelled" }));
      setNotice("Your booking is cancelled and the seats have been released.");
    } catch {
      setError("Could not reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#fdf8f1] text-[#4b2418]">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading your booking…
      </main>
    );
  }

  if (state !== "ready" || !booking) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center bg-[#fdf8f1] px-6 text-center text-[#4b2418]">
        <p className="text-2xl text-[#9d1f1c]">Hari Om 🙏</p>
        <p className="mt-3 max-w-md leading-7">
          {state === "notfound"
            ? "We could not find this booking. The link may have expired, or the booking may already be cancelled."
            : "Something went wrong loading your booking. Please try again shortly."}
        </p>
        <a
          href="https://www.avdheshanandgmission.org"
          className="mt-6 text-sm text-[#9d1f1c] underline-offset-4 hover:underline"
        >
          avdheshanandgmission.org
        </a>
      </main>
    );
  }

  const isCancelled = booking.status === "Cancelled" || booking.status === "Rejected";
  const longDate = formatDate(booking.eventDate);
  const seatsLeft = typeof booking.seatsAvailable === "number" ? booking.seatsAvailable : null;
  const canAddMore = Math.max(0, (booking.maxPerEmail || 6) - (booking.partySize || 0));

  return (
    <main className="min-h-screen bg-[#fdf8f1] py-10 text-[#2a1a12]">
      <div className="mx-auto w-full max-w-2xl px-5">
        <div className="mb-6 flex items-center gap-3">
          <img
            src="/assets/Avdheshanandg mission logo.png"
            alt="AvdheshanandG Mission"
            className="h-9 w-auto"
          />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#9d1f1c]">
            AvdheshanandG Mission
          </span>
        </div>

        <h1 className="text-2xl font-semibold text-[#5a0000]">Your booking</h1>
        <p className="mt-1 text-sm text-[#6b5545]">
          Booked by {booking.name} · {booking.email}
        </p>

        {/* Event summary */}
        <div className="mt-5 rounded-xl border border-[#e6d6bd] bg-white p-5 shadow-sm">
          {booking.eventName && (
            <p className="mb-3 text-base font-semibold text-[#5a0000]">{booking.eventName}</p>
          )}
          <div className="space-y-2 text-sm text-[#4b2418]">
            {longDate && (
              <p className="flex items-center gap-2">
                <Calendar size={16} className="text-[#9d1f1c]" />
                {longDate}
              </p>
            )}
            {booking.eventTime && booking.eventTime !== "To be announced" && (
              <p className="flex items-center gap-2">
                <Clock size={16} className="text-[#9d1f1c]" />
                {booking.eventTime}
              </p>
            )}
            {booking.eventLocation && (
              <p className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 shrink-0 text-[#9d1f1c]" />
                {booking.eventLocation}
              </p>
            )}
            <p className="flex items-center gap-2 pt-1">
              <Users size={16} className="text-[#9d1f1c]" />
              {booking.partySize} {booking.partySize === 1 ? "guest" : "guests"} · {booking.status}
            </p>
          </div>
        </div>

        {isCancelled && (
          <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            This booking is cancelled, so it can no longer be changed. You are welcome to register
            again if seats remain.
          </p>
        )}

        {notice && (
          <p className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
            {notice}
          </p>
        )}
        {error && (
          <p className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {error}
          </p>
        )}

        {!isCancelled && (
          <>
            {/* Guest names */}
            <section className="mt-6 rounded-xl border border-[#e6d6bd] bg-white p-5 shadow-sm">
              <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-[#9d1f1c]">
                Guests
              </h2>
              <p className="mt-1 text-xs text-[#6b5545]">
                Each guest carries their own entry pass. Correct a spelling, or remove someone who
                can no longer attend.
              </p>

              <div className="mt-4 space-y-3">
                {names.map((n, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      value={n}
                      disabled={booking.attendees[i]?.checkedIn || busy}
                      onChange={(e) =>
                        setNames((cur) => cur.map((v, j) => (j === i ? e.target.value : v)))
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30 disabled:bg-gray-100 disabled:text-gray-500"
                    />
                    {booking.attendees[i]?.checkedIn ? (
                      <span className="shrink-0 text-xs font-medium text-green-700">
                        Checked in
                      </span>
                    ) : (
                      names.length > 1 && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Remove ${booking.attendees[i]?.name || "this guest"} and free their seat?`
                              )
                            ) {
                              send({ action: "remove", attendeeIndex: i }, "Guest removed.");
                            }
                          }}
                          className="shrink-0 rounded-md border border-red-200 p-2 text-red-600 hover:bg-red-50 disabled:opacity-50"
                          aria-label={`Remove ${booking.attendees[i]?.name || "guest"}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      )
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                disabled={busy}
                onClick={() => send({ action: "rename", names }, "Names updated.")}
                className="mt-4 rounded-md bg-[#5a0000] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#7a0000] disabled:opacity-60"
              >
                {busy ? "Saving…" : "Save names"}
              </button>
            </section>

            {/* Add people */}
            <section className="mt-5 rounded-xl border border-[#e6d6bd] bg-white p-5 shadow-sm">
              <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-[#9d1f1c]">
                Bring someone else
              </h2>
              <p className="mt-1 text-xs text-[#6b5545]">
                Add them here rather than registering again — that way everyone stays on one
                booking.
                {canAddMore > 0
                  ? ` You can add up to ${canAddMore} more (limit ${booking.maxPerEmail} per email).`
                  : ` You have reached the limit of ${booking.maxPerEmail} people for this email.`}
                {seatsLeft !== null && ` ${seatsLeft} seats remain for the event.`}
              </p>

              {additions.map((n, i) => (
                <input
                  key={i}
                  value={n}
                  disabled={busy}
                  placeholder={`New guest ${i + 1} full name`}
                  onChange={(e) =>
                    setAdditions((cur) => cur.map((v, j) => (j === i ? e.target.value : v)))
                  }
                  className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30"
                />
              ))}

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={busy || additions.length >= canAddMore}
                  onClick={() => setAdditions((cur) => [...cur, ""])}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[#9d1f1c] px-4 py-2 text-sm font-medium text-[#9d1f1c] hover:bg-[#9d1f1c]/5 disabled:opacity-40"
                >
                  <Plus size={16} />
                  Add a guest
                </button>
                {additions.length > 0 && (
                  <button
                    type="button"
                    disabled={busy || additions.every((n) => !n.trim())}
                    onClick={() =>
                      send(
                        { action: "add", names: additions.filter((n) => n.trim()) },
                        "Guests added."
                      )
                    }
                    className="rounded-md bg-[#5a0000] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#7a0000] disabled:opacity-60"
                  >
                    {busy ? "Saving…" : "Confirm new guests"}
                  </button>
                )}
              </div>
            </section>

            {/* Cancel everything. Kept visually quiet and last, so it is never
                mistaken for the ordinary "save my changes" action. */}
            <section className="mt-5 rounded-xl border border-red-200 bg-red-50/50 p-5">
              <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-[#9d1f1c]">
                Cancel this booking
              </h2>
              <p className="mt-1 text-xs text-[#6b5545]">
                This releases all {booking.partySize}{" "}
                {booking.partySize === 1 ? "seat" : "seats"} so another devotee may attend. It
                cannot be undone — you would need to register again.
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={cancelAll}
                className="mt-4 rounded-md border border-red-300 bg-white px-5 py-2.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
              >
                {busy ? "Cancelling…" : "Cancel the whole booking"}
              </button>
            </section>

            <p className="mt-6 text-center text-xs text-[#6b5545]">
              Need help? Write to{" "}
              <a
                href="mailto:office@avdheshanandgmission.org"
                className="text-[#9d1f1c] underline-offset-2 hover:underline"
              >
                office@avdheshanandgmission.org
              </a>
              .
            </p>
          </>
        )}
      </div>
    </main>
  );
}
